import { useState, useEffect, useMemo, useCallback, lazy, Suspense } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, Receipt as ReceiptIcon, TrendingUp, PieChart, LogOut, Menu, Settings, Building2, BarChart3, User, Eye, Filter, CalendarIcon, X } from "lucide-react";
import { format } from "date-fns";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { UploadArea } from "@/components/UploadArea";
import { ReceiptCard } from "@/components/ReceiptCard";
import { CategoryBreakdown } from "@/components/CategoryBreakdown";
import { SpendingChart } from "@/components/SpendingChart";
import { MobileHeader } from "@/components/MobileHeader";
import { RecentReceiptsList } from "@/components/RecentReceiptsList";
import { ReceiptDetails } from "@/components/ReceiptDetails";
import { ReceiptDetailsContent } from "@/components/ReceiptDetailsContent";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/hooks/useAuth";
import { Footer } from "@/components/Footer";
import { CreditsBalance } from "@/components/CreditsBalance";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { useCurrencyConversion } from "@/hooks/useCurrencyConversion";
import { formatCurrency } from "@/lib/currency";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { normalizeCategory } from "@/lib/receiptCategory";
import * as Sentry from "@sentry/react";

const Landing = lazy(() => import("./Landing"));

// Define types first
interface ReceiptItem {
  name: string;
  amount: number;
  category: "food" | "household" | "health" | "personal" | "other";
  quantity: number;
  price: number;
}

interface Receipt {
  id: string; // receipts.id is a UUID
  date: string;
  store: string;
  total: number;
  items: ReceiptItem[];
  imageUrl: string;
  address?: string;
  currency?: string;
  subtotal?: number;
  store_id?: string | null;
}

type SupabaseLineItem = {
  product_name: string;
  price: string | number | null;
  quantity: string | number | null;
  category: string | null;
};

type SupabaseReceiptRow = {
  id: string; // receipts.id is a UUID
  date: string;
  store_name: string;
  total: string | number | null;
  currency?: string | null;
  image_url?: string | null;
  store_address?: string | null;
  store_id?: string | null;
  line_items: SupabaseLineItem[] | null;
};

const Index = () => {
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [isLoadingReceipts, setIsLoadingReceipts] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [showUpload, setShowUpload] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState<Receipt | null>(null);
  const [refreshPending, setRefreshPending] = useState(false);
  
  // Filters and pagination
  const [storeFilter, setStoreFilter] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { preferredCurrency, convertAmount, isLoading: currencyLoading } = useCurrencyConversion();
  const location = useLocation();
  const tabParam = new URLSearchParams(location.search).get("tab");

  useEffect(() => {
    if (!isMobile && tabParam) {
      if (["dashboard", "receipts", "upload"].includes(tabParam)) {
        setActiveTab(tabParam);
      }
    }
  }, [tabParam, isMobile]);

  const fetchReceipts = useCallback(async () => {
    if (!user) return;

    try {
      const { data: receiptsData, error: receiptsError } = await supabase
        .from('receipts')
        .select('*, line_items(*)')
        .order('date', { ascending: false });

      if (receiptsError) throw receiptsError;

      // For private buckets, create a signed URL for the image
      const extractPathFromPublicUrl = (url: string): string | null => {
        try {
          const u = new URL(url);
          const parts = u.pathname.split('/');
          const idx = parts.findIndex((p) => p === 'receipts');
          if (idx !== -1) return decodeURIComponent(parts.slice(idx + 1).join('/'));
          return null;
        } catch {
          return null;
        }
      };

      const typedReceipts: SupabaseReceiptRow[] = (receiptsData ?? []) as SupabaseReceiptRow[];

      const formattedReceipts: Receipt[] = await Promise.all(
        typedReceipts.map(async (receipt) => {
          // Build signed URL if possible
          let imageUrl: string = receipt.image_url ?? '';
          const path = receipt.image_url ? extractPathFromPublicUrl(receipt.image_url) : null;
          if (path) {
            const { data: signed, error: signError } = await supabase
              .storage
              .from('receipts')
              .createSignedUrl(path, 60 * 60); // 1 hour
            if (!signError && signed?.signedUrl) {
              imageUrl = signed.signedUrl;
            }
          }

          return {
            id: receipt.id,
            date: receipt.date,
            store: receipt.store_name,
            total: Number(receipt.total ?? 0),
            currency: receipt.currency || 'USD',
            items: (receipt.line_items ?? []).map((item) => ({
              name: item.product_name,
              amount: Number(item.price ?? 0) * Number(item.quantity ?? 0),
              category: normalizeCategory(item.category),
              quantity: Number(item.quantity ?? 0),
              price: Number(item.price ?? 0)
            })),
            imageUrl,
            address: receipt.store_address,
            store_id: receipt.store_id
          } as Receipt;
        })
      );

      setReceipts(formattedReceipts);
    } catch (error) {
      console.error('Error fetching receipts:', error);

      const sentryEventId = Sentry.captureException(error, {
        level: 'error',
        tags: {
          component: 'Index',
          action: 'fetch_receipts',
          error_code: '5001', // DATABASE_FETCH_FAILED
        },
        extra: {
          userId: user?.id,
          userEmail: user?.email,
          errorMessage: error instanceof Error ? error.message : String(error),
        },
      });

      const supportId = sentryEventId
        ? sentryEventId.substring(0, 8).toUpperCase()
        : `DEV${Date.now().toString(36).toUpperCase().slice(-6)}`;

      toast({
        title: t('errors.title'),
        description: t('errors.fetchReceiptsFailedWithSupport', { supportId }),
        variant: "destructive",
        duration: Infinity,
      });
    } finally {
      setIsLoadingReceipts(false);
    }
  }, [user, toast, t]);

  useEffect(() => {
    fetchReceipts();
  }, [fetchReceipts]);

  useEffect(() => {
    if (!refreshPending) return;

    if (isMobile && showUpload) {
      return;
    }

    let isCancelled = false;

    const refreshData = async () => {
      await new Promise((resolve) => setTimeout(resolve, 500));
      if (isCancelled) return;

      await fetchReceipts();

      if (!isCancelled) {
        setRefreshPending(false);
      }
    };

    refreshData();

    return () => {
      isCancelled = true;
    };
  }, [refreshPending, isMobile, showUpload, fetchReceipts]);

  // Get unique stores for filter
  const uniqueStores = useMemo(() => {
    const stores = Array.from(new Set(receipts.map(r => r.store)));
    return stores.sort();
  }, [receipts]);

  // Filter and paginate receipts
  const filteredReceipts = useMemo(() => {
    let filtered = receipts;

    // Store filter
    if (storeFilter !== "all") {
      filtered = filtered.filter(r => r.store === storeFilter);
    }

    // Date range filter
    if (dateFrom) {
      filtered = filtered.filter(r => new Date(r.date) >= dateFrom);
    }
    if (dateTo) {
      filtered = filtered.filter(r => new Date(r.date) <= dateTo);
    }

    return filtered;
  }, [receipts, storeFilter, dateFrom, dateTo]);

  const paginatedReceipts = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredReceipts.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredReceipts, currentPage]);

  const totalPages = Math.ceil(filteredReceipts.length / itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [storeFilter, dateFrom, dateTo]);

  const totalSpent = receipts.reduce((sum, receipt) => {
    const convertedAmount = convertAmount(receipt.total, receipt.currency || 'USD');
    return sum + convertedAmount;
  }, 0);
  const totalReceipts = receipts.length;

  const handleSignOut = async () => {
    await signOut();
    toast({
      title: t('toast.success'),
      description: t('profile.accountInformation'),
    });
    navigate("/auth");
  };

  // Logged-out visitors see the marketing landing page. This must run before the
  // loading gate below: receipts/currency loading flags never resolve without a user.
  if (!loading && !user) {
    return (
      <Suspense fallback={<div className="min-h-screen bg-paper" />}>
        <Landing />
      </Suspense>
    );
  }

  if (loading || isLoadingReceipts || currencyLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }


  // Mobile Receipts page when tab=receipts
  if (isMobile && tabParam === 'receipts') {
    return (
      <div className="min-h-screen bg-background">
        <MobileHeader
          onUploadClick={() => setShowUpload(true)}
          onSignOut={handleSignOut}
          userEmail={user?.email}
        />

        <div className="p-4 space-y-6">
          <h2 className="text-lg font-semibold">{t('dashboard.allReceipts')}</h2>
          <div className="grid grid-cols-1 gap-4">
            {receipts.map((receipt) => (
              <ReceiptCard key={receipt.id} receipt={receipt} />
            ))}
          </div>
        </div>

        {/* Upload Dialog */}
        <Dialog open={showUpload} onOpenChange={setShowUpload}>
          <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-y-auto">
            <UploadArea
              onUpload={(file) => {
                console.log('File uploaded:', file);
                setRefreshPending(true);
              }}
            />
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Mobile view
  if (isMobile) {
    return (
      <div className="min-h-screen bg-background">
        <MobileHeader
          onUploadClick={() => setShowUpload(true)}
          onSignOut={handleSignOut}
          userEmail={user?.email}
        />

        <div className="p-4 space-y-6">
          {/* Total Spend - Priority 3 */}
          <Card className="shadow-custom-lg border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-primary-foreground/80 font-medium">{t('dashboard.totalSpending')}</p>
                  <p className="text-3xl font-bold text-primary-foreground mt-1">
                    {formatCurrency(totalSpent, preferredCurrency)}
                  </p>
                  <p className="text-xs text-primary-foreground/70 mt-1">
                    {totalReceipts} {totalReceipts === 1 ? t('dashboard.receiptsCount', { count: 1 }) : t('dashboard.receiptsCount', { count: totalReceipts })}
                  </p>
                </div>
                <TrendingUp className="h-12 w-12 text-primary-foreground/50" />
              </div>
            </CardContent>
          </Card>

          {/* Upload CTA - Priority 1 */}
          <Button
            size="lg"
            className="w-full h-14 transition-all duration-300 text-base font-semibold"
            onClick={() => setShowUpload(true)}
          >
            <Upload className="mr-2 h-5 w-5" />
            {t('buttons.uploadReceipt')}
          </Button>

          {/* Recent Receipts - Priority 2 */}
          <div>
            <h2 className="text-lg font-semibold mb-3">{t('dashboard.recentReceipts')}</h2>
            <RecentReceiptsList
              receipts={receipts}
              onReceiptClick={(receipt) => setSelectedReceipt(receipt)}
            />
          </div>
        </div>

        {/* Upload Dialog */}
        <Dialog open={showUpload} onOpenChange={setShowUpload}>
          <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-y-auto">
            <UploadArea
              onUpload={(file) => {
                console.log('File uploaded:', file);
                setRefreshPending(true);
              }}
            />
          </DialogContent>
        </Dialog>

        {/* Receipt Details Dialog */}
        <Dialog open={!!selectedReceipt} onOpenChange={() => setSelectedReceipt(null)}>
          <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-auto flex flex-col">
            <DialogTitle className="sr-only">{t('receipt.details')}</DialogTitle>
            <DialogDescription className="sr-only">{t('receipt.detailsDescription')}</DialogDescription>
            {selectedReceipt && (
              <ReceiptDetailsContent
                receipt={selectedReceipt} 
                onReanalyze={async () => {
                  await fetchReceipts();
                  setSelectedReceipt(null);
                }}
                onDelete={async () => {
                  await fetchReceipts();
                  setSelectedReceipt(null);
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Desktop view
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b shadow-custom-sm">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-72">
                  <SheetHeader>
                    <SheetTitle className="text-left">{t('common.menu')}</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6 flex flex-col gap-2">
                    <div
                      className="mb-4 rounded-lg bg-accent p-4 cursor-pointer hover:bg-accent/80 transition-colors"
                      onClick={() => navigate('/profile')}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary">
                          <User className="h-5 w-5 text-primary-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{user?.email || "User"}</p>
                          <p className="text-xs text-muted-foreground">{t('navigation.profile')}</p>
                        </div>
                      </div>
                    </div>

                    <Button
                      variant="ghost"
                      className="justify-start"
                      onClick={() => {
                        setActiveTab("receipts");
                      }}
                    >
                      <ReceiptIcon className="mr-3 h-4 w-4" />
                      {t('navigation.receipts')}
                    </Button>

                    <Button
                      variant="ghost"
                      className="justify-start"
                      onClick={() => navigate('/stores')}
                    >
                      <Building2 className="mr-3 h-4 w-4" />
                      {t('navigation.stores')}
                    </Button>

                    <Button variant="ghost" className="justify-start" disabled>
                      <BarChart3 className="mr-3 h-4 w-4" />
                      {t('navigation.analytics')}
                      <span className="ml-auto text-xs text-muted-foreground">{t('common.comingSoon')}</span>
                    </Button>

                    <Button
                      variant="ghost"
                      className="justify-start"
                      onClick={() => navigate('/profile')}
                    >
                      <Settings className="mr-3 h-4 w-4" />
                      {t('navigation.settings')}
                    </Button>

                    <Button
                      variant="ghost"
                      className="justify-start text-destructive hover:text-destructive"
                      onClick={handleSignOut}
                    >
                      <LogOut className="mr-3 h-4 w-4" />
                      {t('common.signOut')}
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
              
              <div>
                <h1
                  className="text-3xl font-bold bg-clip-text text-transparent cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => navigate('/')}
                >
                  {t('common.appName')}
                </h1>
                <p className="text-muted-foreground mt-1">
                  {t('common.appDescription')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <CreditsBalance />
              <Button
                size="lg"
                className="transition-all duration-300"
                onClick={() => setActiveTab("upload")}
              >
                <Upload className="mr-2 h-4 w-4" />
                {t('buttons.uploadReceipt')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="shadow-custom-md border-0">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('dashboard.totalSpent')}</CardTitle>
              <TrendingUp className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(totalSpent, preferredCurrency)}</div>
              <p className="text-xs text-muted-foreground">
                {t('dashboard.allAmountsIn', { currency: preferredCurrency })}
              </p>
            </CardContent>
          </Card>

          <Card
            className="shadow-custom-md border-0 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setActiveTab('receipts')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('dashboard.receiptsCount')}</CardTitle>
              <ReceiptIcon className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalReceipts}</div>
              <p className="text-xs text-muted-foreground">
                {t('dashboard.thisMonth')}
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-custom-md border-0">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('category.title')}</CardTitle>
              <PieChart className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">5</div>
              <p className="text-xs text-muted-foreground">
                {t('dashboard.activeCategories')}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="dashboard" className="space-y-6" value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="dashboard">{t('tabs.dashboard')}</TabsTrigger>
            <TabsTrigger value="receipts">{t('tabs.receipts')}</TabsTrigger>
            <TabsTrigger value="upload">{t('tabs.upload')}</TabsTrigger>
          </TabsList>
          
          <TabsContent value="dashboard" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <CategoryBreakdown 
                receipts={receipts} 
                preferredCurrency={preferredCurrency}
                convertAmount={convertAmount}
              />
              <SpendingChart 
                receipts={receipts}
                preferredCurrency={preferredCurrency}
                convertAmount={convertAmount}
              />
            </div>
          </TabsContent>
          
          <TabsContent value="receipts" className="space-y-6">
            <Card className="shadow-custom-md border-0">
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <CardTitle>{t('dashboard.allReceipts')}</CardTitle>
                    <CardDescription>
                      {t('dashboard.receiptsFound', { count: filteredReceipts.length })}
                    </CardDescription>
                  </div>

                  {/* Filters */}
                  <div className="flex flex-wrap items-center gap-2">
                    <Select value={storeFilter} onValueChange={setStoreFilter}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder={t('dashboard.allStores')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('dashboard.allStores')}</SelectItem>
                        {uniqueStores.map((store) => (
                          <SelectItem key={store} value={store}>
                            {store}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <div className="flex items-center gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "justify-start text-left font-normal",
                              !dateFrom && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {dateFrom ? format(dateFrom, "MMM dd, yyyy") : t('dashboard.fromDate')}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={dateFrom}
                            onSelect={setDateFrom}
                            initialFocus
                            className="pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>

                      <span className="text-muted-foreground">{t('dashboard.to')}</span>

                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "justify-start text-left font-normal",
                              !dateTo && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {dateTo ? format(dateTo, "MMM dd, yyyy") : t('dashboard.toDate')}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={dateTo}
                            onSelect={setDateTo}
                            initialFocus
                            className="pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>

                      {(dateFrom || dateTo) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setDateFrom(undefined);
                            setDateTo(undefined);
                          }}
                          title={t('dashboard.clearDateFilter')}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Table */}
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('table.date')}</TableHead>
                        <TableHead>{t('table.store')}</TableHead>
                        <TableHead>{t('table.items')}</TableHead>
                        <TableHead className="text-right">{t('table.total')}</TableHead>
                        <TableHead className="text-right">{t('table.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedReceipts.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                            {t('dashboard.receiptsFound', { count: 0 })}
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedReceipts.map((receipt) => (
                          <TableRow 
                            key={receipt.id}
                            className="cursor-pointer hover:bg-accent/50 transition-colors"
                            onClick={() => setSelectedReceipt(receipt)}
                          >
                            <TableCell className="font-medium">
                              {new Date(receipt.date).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              {receipt.store}
                            </TableCell>
                            <TableCell>{t('dashboard.itemCount', { count: receipt.items.length })}</TableCell>
                            <TableCell className="text-right font-semibold">
                              {formatCurrency(receipt.total, receipt.currency || 'USD')}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedReceipt(receipt);
                                }}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                {t('buttons.viewDetails')}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-4">
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                          />
                        </PaginationItem>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <PaginationItem key={page}>
                            <PaginationLink
                              onClick={() => setCurrentPage(page)}
                              isActive={currentPage === page}
                              className="cursor-pointer"
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        <PaginationItem>
                          <PaginationNext
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Receipt Details Dialog */}
            <Dialog open={!!selectedReceipt} onOpenChange={() => setSelectedReceipt(null)}>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto flex flex-col">
                <DialogTitle className="sr-only">{t('receipt.details')}</DialogTitle>
                <DialogDescription className="sr-only">{t('receipt.detailsDescription')}</DialogDescription>
                {selectedReceipt && (
                  <ReceiptDetailsContent
                    receipt={selectedReceipt} 
                    onReanalyze={async () => {
                      await fetchReceipts();
                      setSelectedReceipt(null);
                    }}
                    onDelete={async () => {
                      await fetchReceipts();
                      setSelectedReceipt(null);
                    }}
                  />
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>
          
          <TabsContent value="upload" className="space-y-6">
            <UploadArea onUpload={(file) => {
              console.log('File uploaded:', file);
              setRefreshPending(true);
            }} />
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
};

export default Index;